import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sendVirtualMidiMessage } from '../services/webMidi';

interface VirtualPianoKeyboardProps {
  initialLeft?: number;
  initialTop?: number;
  onClose?: () => void;
  variant?: 'floating' | 'inline';
}

type ActiveNote = {
  oscillator: OscillatorNode;
  gain: GainNode;
  stopTimeout: number | null;
};

let sharedAudioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  try {
    if (!sharedAudioContext) {
      const Ctor = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
      if (!Ctor) return null;
      sharedAudioContext = new Ctor();
    }
    return sharedAudioContext;
  } catch (err) {
    console.warn('[VirtualPianoKeyboard] Failed to create AudioContext:', err);
    return null;
  }
}

function midiToFrequency(note: number): number {
  // Standard 12-TET: A4 (69) = 440 Hz
  return 440 * Math.pow(2, (note - 69) / 12);
}

// Build a 49-key layout from C3 (48) through C7 (96) inclusive.
const FIRST_MIDI_NOTE = 48; // C3
const KEY_COUNT = 49;
const LAST_MIDI_NOTE = FIRST_MIDI_NOTE + KEY_COUNT - 1; // 96 (C7)

type PianoKey = {
  midi: number;
  isBlack: boolean;
  label: string;
};

type KeySmasherAssignment = {
  label: string;
  side: 'left' | 'right'; // left = Player 1 cluster, right = Player 2 cluster
};

// Explicit Key Smashers layout:
// Player 1 (left side) uses notes centered around E3/B4,
// Player 2 (right side) mirrors two octaves higher (E5/B6).
// This keeps MIDI numbers and Web MIDI integration consistent
// while matching the physical keyboard layout in the design.
const KEY_SMASHER_NOTE_MAP: Record<string, KeySmasherAssignment> = {
  // Player 1 – left hand (movement)
  E3: { label: 'Crouch', side: 'left' },
  G3: { label: 'Left', side: 'left' },
  A3: { label: 'Jump', side: 'left' },
  B3: { label: 'Right', side: 'left' },

  // Player 1 – right hand (attacks)
  E4: { label: 'Hands 1', side: 'left' },
  'F#4': { label: 'Hands 2', side: 'left' },
  G4: { label: 'Hands 3', side: 'left' },
  A4: { label: 'Foot 1', side: 'left' },
  B4: { label: 'Foot 2', side: 'left' },

  // Player 2 – left hand (movement) – two octaves up
  E5: { label: 'Crouch', side: 'right' },
  G5: { label: 'Left', side: 'right' },
  A5: { label: 'Jump', side: 'right' },
  B5: { label: 'Right', side: 'right' },

  // Player 2 – right hand (attacks) – two octaves up
  E6: { label: 'Hands 1', side: 'right' },
  'F#6': { label: 'Hands 2', side: 'right' },
  G6: { label: 'Hands 3', side: 'right' },
  A6: { label: 'Foot 1', side: 'right' },
  B6: { label: 'Foot 2', side: 'right' },
};

function buildKeys(): PianoKey[] {
  const keys: PianoKey[] = [];

  for (let midi = FIRST_MIDI_NOTE; midi <= LAST_MIDI_NOTE; midi++) {
    const pitchClass = midi % 12;
    const octave = Math.floor(midi / 12) - 1;

    const isBlack =
      pitchClass === 1 || // C#
      pitchClass === 3 || // D#
      pitchClass === 6 || // F#
      pitchClass === 8 || // G#
      pitchClass === 10;  // A#

    let baseName: string;
    switch (pitchClass) {
      case 0: baseName = 'C'; break;
      case 1: baseName = 'C#'; break;
      case 2: baseName = 'D'; break;
      case 3: baseName = 'D#'; break;
      case 4: baseName = 'E'; break;
      case 5: baseName = 'F'; break;
      case 6: baseName = 'F#'; break;
      case 7: baseName = 'G'; break;
      case 8: baseName = 'G#'; break;
      case 9: baseName = 'A'; break;
      case 10: baseName = 'A#'; break;
      case 11: baseName = 'B'; break;
      default: baseName = '?';
    }

    keys.push({
      midi,
      isBlack,
      label: `${baseName}${octave}`,
    });
  }

  return keys;
}

const ALL_KEYS: PianoKey[] = buildKeys();
const WHITE_KEYS: PianoKey[] = ALL_KEYS.filter(k => !k.isBlack);
const BLACK_KEYS: PianoKey[] = ALL_KEYS.filter(k => k.isBlack);

const VirtualPianoKeyboard: React.FC<VirtualPianoKeyboardProps> = ({
  initialLeft = 260,
  initialTop = 120,
  onClose,
  variant = 'floating',
}) => {
  const [position, setPosition] = useState({ left: initialLeft, top: initialTop });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeNotesRef = useRef<Map<number, ActiveNote>>(new Map());
  // Default to Key Smasher mode for inline keyboards (used on the Key Smashers page).
  const [keySmasherMode, setKeySmasherMode] = useState(variant === 'inline');

  const whiteKeys = useMemo(() => WHITE_KEYS, []);
  const blackKeys = useMemo(() => BLACK_KEYS, []);

  const keySmasherAssignments = useMemo(() => {
    const map: Record<number, KeySmasherAssignment> = {};
    ALL_KEYS.forEach((key) => {
      const assignment = KEY_SMASHER_NOTE_MAP[key.label];
      if (assignment) {
        map[key.midi] = assignment;
      }
    });
    return map;
  }, []);

  const sendMidi = useCallback((midi: number, isNoteOn: boolean) => {
    const status = isNoteOn ? 144 : 128; // Note On / Note Off, channel 1
    const velocity = isNoteOn ? 100 : 0;
    sendVirtualMidiMessage({ status, note: midi, velocity });
  }, []);

  const stopNoteAudio = useCallback((midi: number) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    const map = activeNotesRef.current;
    const active = map.get(midi);
    if (!active) return;

    try {
      const now = ctx.currentTime;
      active.gain.gain.cancelScheduledValues(now);
      active.gain.gain.setValueAtTime(active.gain.gain.value, now);
      active.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
      active.oscillator.stop(now + 0.15);
    } catch {
      // ignore
    }

    if (active.stopTimeout !== null) {
      window.clearTimeout(active.stopTimeout);
    }

    map.delete(midi);
  }, []);

  const startNoteAudio = useCallback((midi: number) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    const map = activeNotesRef.current;
    if (map.has(midi)) {
      // restart envelope
      stopNoteAudio(midi);
    }

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.value = midiToFrequency(midi);

    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    // Louder envelope so the piano can compete with in-game music
    gain.gain.linearRampToValueAtTime(0.7, now + 0.03);
    gain.gain.linearRampToValueAtTime(0.5, now + 0.18);

    osc.start(now);

    const stopTimeout = window.setTimeout(() => {
      stopNoteAudio(midi);
    }, 1200);

    map.set(midi, { oscillator: osc, gain, stopTimeout });
  }, [stopNoteAudio]);

  const handleKeyPress = useCallback(
    (midi: number, isDown: boolean) => {
      console.log(
        '[VirtualPianoKeyboard] key',
        isDown ? 'DOWN' : 'UP',
        'midi=',
        midi,
        'variant=',
        variant
      );
      if (isDown) {
        startNoteAudio(midi);
        sendMidi(midi, true);
      } else {
        stopNoteAudio(midi);
        sendMidi(midi, false);
      }
    },
    [sendMidi, startNoteAudio, stopNoteAudio]
  );

  const handleMouseDownKey = (midi: number, event: React.MouseEvent) => {
    event.preventDefault();
    handleKeyPress(midi, true);

    const handleUp = (e: MouseEvent) => {
      e.preventDefault();
      handleKeyPress(midi, false);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('mouseleave', handleUp);
    };

    window.addEventListener('mouseup', handleUp);
    window.addEventListener('mouseleave', handleUp);
  };

  const handleTouchStartKey = (midi: number, event: React.TouchEvent) => {
    event.preventDefault();
    handleKeyPress(midi, true);

    const handleEnd = () => {
      handleKeyPress(midi, false);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleEnd);
    };

    window.addEventListener('touchend', handleEnd, { passive: false });
    window.addEventListener('touchcancel', handleEnd, { passive: false });
  };

  const beginDrag = (clientX: number, clientY: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    setIsDragging(true);
  };

  const handleDragStartMouse = (event: React.MouseEvent) => {
    event.preventDefault();
    beginDrag(event.clientX, event.clientY);
  };

  const handleDragStartTouch = (event: React.TouchEvent) => {
    event.preventDefault();
    const t = event.touches[0];
    if (!t) return;
    beginDrag(t.clientX, t.clientY);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      let clientX: number;
      let clientY: number;

      if ('touches' in event && event.touches) {
        const t = event.touches[0];
        if (!t) return;
        clientX = t.clientX;
        clientY = t.clientY;
      } else {
        clientX = (event as MouseEvent).clientX;
        clientY = (event as MouseEvent).clientY;
      }

      const { x, y } = dragOffsetRef.current;
      const nextLeft = clientX - x;
      const nextTop = clientY - y;

      setPosition({
        left: Math.max(40, Math.min(window.innerWidth - 520, nextLeft)),
        top: Math.max(40, Math.min(window.innerHeight - 260, nextTop)),
      });
    };

    const handleUp = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      setIsDragging(false);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove as any);
      window.removeEventListener('touchend', handleUp as any);
      window.removeEventListener('touchcancel', handleUp as any);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove as any, { passive: false });
    window.addEventListener('touchend', handleUp as any, { passive: false });
    window.addEventListener('touchcancel', handleUp as any, { passive: false });

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove as any);
      window.removeEventListener('touchend', handleUp as any);
      window.removeEventListener('touchcancel', handleUp as any);
    };
  }, [isDragging]);

  useEffect(() => {
    return () => {
      const ctx = getAudioContext();
      if (!ctx) return;
      const map = activeNotesRef.current;
      map.forEach((active, midi) => {
        try {
          const now = ctx.currentTime;
          active.gain.gain.cancelScheduledValues(now);
          active.gain.gain.setValueAtTime(active.gain.gain.value, now);
          active.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
          active.oscillator.stop(now + 0.1);
        } catch {
          // ignore
        }
        if (active.stopTimeout !== null) {
          window.clearTimeout(active.stopTimeout);
        }
        map.delete(midi);
      });
    };
  }, []);

  // While the piano is open, block right-click / two-finger tap context menus
  // and multi-touch gestures on Chromebooks, but only when the event starts
  // inside the piano container.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleContextMenu = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (containerRef.current && target && containerRef.current.contains(target)) {
        event.preventDefault();
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      const target = event.target as Node | null;
      if (!containerRef.current || !target || !containerRef.current.contains(target)) {
        return;
      }
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('touchstart', handleTouchStart as EventListener, { passive: false });

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('touchstart', handleTouchStart as EventListener);
    };
  }, []);

  const isFloating = variant === 'floating';
  // Slightly narrower keys for inline mode so all labeled notes fit inside the Key Smashers frame.
  const whiteKeyWidth = isFloating ? 40 : 37;
  const baseWidth = whiteKeys.length * whiteKeyWidth + 80;

  return (
    <div
      ref={containerRef}
      style={{
        position: isFloating ? 'fixed' : 'relative',
        left: isFloating ? position.left : undefined,
        top: isFloating ? position.top : undefined,
        width: isFloating ? baseWidth : '100%',
        maxWidth: isFloating ? 'none' : baseWidth,
        margin: isFloating ? undefined : '0 auto',
        background: 'linear-gradient(135deg, rgba(13,71,161,0.92), rgba(79,195,247,0.9))',
        borderRadius: 20,
        boxShadow: '0 18px 45px rgba(0,0,0,0.35)',
        border: '1px solid rgba(255,255,255,0.6)',
        zIndex: isFloating ? 1500 : 'auto',
        color: '#fff',
        userSelect: 'none',
        touchAction: 'none',
        overflow: isFloating ? 'hidden' : 'auto',
        resize: isFloating ? undefined : 'both',
        backdropFilter: 'blur(8px)',
        opacity: 0.96,
      }}
    >
      <div
        onMouseDown={isFloating ? handleDragStartMouse : undefined}
        onTouchStart={isFloating ? handleDragStartTouch : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.5rem 0.85rem',
          cursor: isFloating ? 'grab' : 'default',
          background: 'linear-gradient(90deg, rgba(255,255,255,0.18), rgba(255,255,255,0.06))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              if (onClose) onClose();
            }}
            onTouchEnd={(event) => {
              event.stopPropagation();
              event.preventDefault();
              if (onClose) onClose();
            }}
            style={{
              width: 44,
              height: 44,
              borderRadius: '999px',
              border: '2px solid #ff5252',
              background: '#ff1744',
              color: '#fff',
              fontWeight: 900,
              fontSize: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px rgba(0,0,0,0.25)',
              flexShrink: 0,
            }}
            aria-label="Close piano"
          >
            ×
          </button>
          <span style={{ fontSize: '1.15rem' }}>🎹</span>
          <span style={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: 0.5 }}>
            Overture Mini Piano
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setKeySmasherMode((prev) => !prev);
            }}
            style={{
              borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.8)',
              padding: '0.15rem 0.75rem',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              background: keySmasherMode
                ? 'linear-gradient(135deg, #ff8a80, #ff5252)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.05))',
              color: keySmasherMode ? '#fff' : 'rgba(255,255,255,0.95)',
              boxShadow: keySmasherMode ? '0 0 12px rgba(255,82,82,0.7)' : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            <span style={{ fontSize: '0.8rem' }}>Key Smasher Mode</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 44,
              height: 44,
              borderRadius: '999px',
              border: '2px solid #ff5252',
              background: '#ff1744',
              color: '#fff',
              fontWeight: 900,
              fontSize: 24,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px rgba(0,0,0,0.25)',
            }}
            aria-label="Close piano"
          >
            ×
          </button>
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          padding: '0.75rem 1rem 1rem 1rem',
          background: 'radial-gradient(circle at 0 0, rgba(255,255,255,0.22), rgba(13,71,161,0.95))',
        }}
      >
        <div
          style={{
            position: 'relative',
            height: 170,
            borderRadius: 14,
            background: 'linear-gradient(180deg, rgba(253,253,253,0.92), rgba(229,237,245,0.9))',
            boxShadow: '0 8px 20px rgba(0,0,0,0.35) inset',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'stretch',
              padding: '0 10px 10px 10px',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                position: 'relative',
                flex: '1 1 auto',
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              {/* White keys */}
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-end',
                  height: '100%',
                }}
              >
                {whiteKeys.map((key) => {
                  const assignment = keySmasherAssignments[key.midi];
                  const isKeySmasherKey = keySmasherMode && !!assignment;
                  const highlightColor =
                    assignment?.side === 'left'
                      ? 'rgba(129, 212, 250, 1)'
                      : 'rgba(255, 138, 128, 1)';

                  // Derive the note letter from the label (e.g. C4 -> C, F#3 -> F).
                  const noteLetter = key.label.replace(/[0-9]/g, '').replace('#', '') || ' ';

                  return (
                    <button
                      key={key.midi}
                      type="button"
                      onMouseDown={(e) => handleMouseDownKey(key.midi, e)}
                      onTouchStart={(e) => handleTouchStartKey(key.midi, e)}
                      style={{
                        width: whiteKeyWidth,
                        marginRight: 2,
                        flex: `0 0 ${whiteKeyWidth}px`,
                        height: '100%',
                        borderRadius: '0 0 8px 8px',
                        border: isKeySmasherKey
                          ? '2px solid rgba(0,0,0,0.55)'
                          : '1px solid rgba(0,0,0,0.35)',
                        background: isKeySmasherKey
                          ? `linear-gradient(180deg, ${highlightColor}, #ffffff)`
                          : 'linear-gradient(180deg, #ffffff, #f5f7fa)',
                        boxShadow: isKeySmasherKey
                          ? '0 0 0 1px rgba(0,0,0,0.5), 0 6px 10px rgba(0,0,0,0.5)'
                          : '0 4px 6px rgba(0,0,0,0.35)',
                        position: 'relative',
                        padding: 0,
                      }}
                    >
                      {isKeySmasherKey && assignment && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '68%',
                            left: '50%',
                            right: 'auto',
                            width: '100%',
                            transform: 'translate(-50%, -50%) rotate(-90deg)',
                            transformOrigin: 'center',
                            textAlign: 'center',
                            fontSize: 14,
                            lineHeight: 1.1,
                            color: '#1a237e',
                            fontWeight: 900,
                            textShadow: '0 1px 2px rgba(255,255,255,0.9)',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {assignment.label}
                        </span>
                      )}
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 6,
                          left: 0,
                          right: 0,
                          textAlign: 'center',
                          fontSize: 10,
                          color: '#4b5b6b',
                          fontWeight: 600,
                        }}
                      >
                        {noteLetter}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Black keys */}
              <div
                style={{
                  position: 'absolute',
                  inset: '0 10px 40px 10px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  pointerEvents: 'none',
                }}
              >
                {blackKeys.map((key) => {
                  const assignment = keySmasherAssignments[key.midi];
                  const isKeySmasherKey = keySmasherMode && !!assignment;
                  const semitoneIndex = key.midi - FIRST_MIDI_NOTE;
                  let approxWhiteIndex = Math.round(semitoneIndex * (7 / 12)) - 1;
                  if (approxWhiteIndex < 0) approxWhiteIndex = 0;
                  const left = approxWhiteIndex * (whiteKeyWidth + 2) + whiteKeyWidth * 0.65;

                  return (
                    <button
                      key={key.midi}
                      type="button"
                      onMouseDown={(e) => handleMouseDownKey(key.midi, e)}
                      onTouchStart={(e) => handleTouchStartKey(key.midi, e)}
                      style={{
                        position: 'absolute',
                        left,
                        width: whiteKeyWidth * 0.7,
                        height: '70%',
                        marginLeft: -whiteKeyWidth * 0.35,
                        borderRadius: '0 0 6px 6px',
                        border: '1px solid rgba(0,0,0,0.6)',
                        background: isKeySmasherKey
                          ? 'linear-gradient(180deg, #212121, #000000)'
                          : 'linear-gradient(180deg, #1e1e24, #050509)',
                        boxShadow: isKeySmasherKey
                          ? '0 0 0 1px rgba(255,255,255,0.8), 0 6px 12px rgba(0,0,0,0.9)'
                          : '0 6px 12px rgba(0,0,0,0.7)',
                        padding: 0,
                        pointerEvents: 'auto',
                      }}
                    >
                      {isKeySmasherKey && assignment && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            right: 'auto',
                            width: '100%',
                            transform: 'translate(-50%, -50%) rotate(-90deg)',
                            transformOrigin: 'center',
                            textAlign: 'center',
                            fontSize: 12,
                            lineHeight: 1.1,
                            color: '#ffffff',
                            fontWeight: 900,
                            textShadow: '0 1px 2px rgba(0,0,0,0.9)',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {assignment.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualPianoKeyboard;


